import { useEffect, useRef } from 'react';

// Swipe-down-to-close for a mobile bottom sheet. Every sheet in the app draws
// a grab handle, which promises this gesture; without it the only way out was
// the × (owner, 2026-10-05, on the /suppliers supplier sheet).
//
// The sheet follows the finger downward and closes when it is dragged far
// enough or flicked; a short drag springs back. A drag never starts when:
//   - the finger first moves up or sideways (that is a scroll, not a dismiss);
//   - the touch began inside a scroller that is not at its top (the content
//     scrolls back up first, then a fresh drag can close — the usual iOS feel).
//
// Native listeners, not React's: React registers touchmove as passive, so it
// could not stop the page behind the sheet from scrolling during the drag.
//
//   sheetRef    — the sheet element (the part that moves)
//   onClose     — called once the sheet is off screen
//   backdropRef — optional; fades with the drag
export const CLOSE_DISTANCE = 96;  // px dragged down that closes on release
export const CLOSE_VELOCITY = 0.5; // px per ms — a quick flick closes from less…
export const MIN_FLICK = 48;       // …but never a short tug (pull-to-refresh habit)
const FLICK_WINDOW = 100;          // ms: flick speed is measured over the end of the gesture
const START_SLOP = 6;              // px before deciding scroll vs drag
// A swipe that starts on a text field is the iOS habit for hiding the keyboard,
// not for closing the sheet.
const TEXT_ENTRY = 'input, textarea, select, [contenteditable]';

// Includes the sheet itself: the date-picker and payment sheets ARE the
// scroller, so a sheet scrolled down must scroll back up before it can close.
const scrolledAway = (target, sheet) => {
  for (let el = target; el; el = el.parentElement) {
    if (el.scrollTop > 0) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return true;
    }
    if (el === sheet) break;
  }
  return false;
};

const reducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useSheetDrag(sheetRef, onClose, { enabled = true, backdropRef } = {}) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!enabled || !sheet) return undefined;
    let state = 'idle'; // idle | pending | dragging | ignore
    let startX = 0, startY = 0, dy = 0, closeTimer = null;
    let samples = []; // [time, dy] while dragging, for the flick speed

    const place = (y, animate) => {
      const transition = animate && !reducedMotion() ? 'transform .2s ease-out, opacity .2s ease-out' : 'none';
      sheet.style.transition = transition;
      // The open animation (docsSheetUp / paymentSheetIn) also animates
      // transform and would override the finger during its first ~220ms.
      sheet.style.animation = 'none';
      sheet.style.transform = y ? `translateY(${y}px)` : '';
      const backdrop = backdropRef && backdropRef.current;
      if (backdrop) {
        backdrop.style.transition = transition;
        backdrop.style.opacity = y ? String(Math.max(0, 1 - y / (sheet.offsetHeight || 400))) : '';
      }
    };

    // Hand the sheet back to its resting place and stop tracking this touch.
    const abandon = () => {
      if (state === 'dragging') place(0, true);
      state = 'ignore';
    };

    const onStart = (e) => {
      if (e.touches.length !== 1) { abandon(); return; } // a second finger
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dy = 0;
      samples = [[Date.now(), 0]];
      const onField = e.target.closest && e.target.closest(TEXT_ENTRY);
      state = onField || scrolledAway(e.target, sheet) ? 'ignore' : 'pending';
    };

    const onMove = (e) => {
      if (state !== 'pending' && state !== 'dragging') return;
      const ddx = e.touches[0].clientX - startX;
      const ddy = e.touches[0].clientY - startY;
      if (state === 'pending') {
        if (Math.abs(ddx) < START_SLOP && Math.abs(ddy) < START_SLOP) return;
        // Up or sideways is a scroll — so is a diagonal swipe (a sideways chip
        // row), so it must be clearly downward. So is a move the browser
        // already owns (not cancelable): taking over then only logs a warning.
        if (ddy <= 0 || Math.abs(ddy) < 1.5 * Math.abs(ddx) || !e.cancelable) { state = 'ignore'; return; }
        state = 'dragging';
      }
      dy = Math.max(0, ddy);
      const now = Date.now();
      samples.push([now, dy]);
      while (samples.length > 2 && now - samples[0][0] > FLICK_WINDOW) samples.shift();
      if (e.cancelable) e.preventDefault(); // the sheet moves, not the page or the sheet's content
      place(dy, false);
    };

    const onEnd = () => {
      if (state !== 'dragging') { state = 'idle'; return; }
      state = 'idle';
      const [t0, y0] = samples[0] || [Date.now(), 0];
      const velocity = (dy - y0) / Math.max(1, Date.now() - t0); // over the last ~100ms
      if (dy > CLOSE_DISTANCE || (dy > MIN_FLICK && velocity > CLOSE_VELOCITY)) {
        place(sheet.offsetHeight || window.innerHeight, true);
        closeTimer = setTimeout(() => closeRef.current(), reducedMotion() ? 0 : 180);
      } else {
        place(0, true);
      }
    };

    sheet.addEventListener('touchstart', onStart, { passive: true });
    sheet.addEventListener('touchmove', onMove, { passive: false });
    sheet.addEventListener('touchend', onEnd);
    // An interrupted touch (a notification, a system gesture) never closes.
    const onCancel = () => { abandon(); state = 'idle'; };
    sheet.addEventListener('touchcancel', onCancel);
    return () => {
      clearTimeout(closeTimer);
      sheet.removeEventListener('touchstart', onStart);
      sheet.removeEventListener('touchmove', onMove);
      sheet.removeEventListener('touchend', onEnd);
      sheet.removeEventListener('touchcancel', onCancel);
    };
  }, [sheetRef, enabled, backdropRef]);
}
